import { debounce } from '@theme/utilities';

const MIN_CHARS = 2;
const PRODUCT_LIMIT = 4;

/**
 * Live suggestions under the search page input: suggested terms and a few products, fetched
 * from the predictive search API while the visitor types. Enter still runs the full search.
 *
 * @attr data-url - The predictive search route (routes.predictive_search_url)
 * @attr data-search-url - The search route (routes.search_url)
 * @attr data-currency - ISO code used to format prices
 */
class SearchPageSuggestions extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {AbortController | null} */
  #request = null;

  connectedCallback() {
    this.input = /** @type {HTMLInputElement | null} */ (
      this.closest('.search-page__header')?.querySelector('.search-page-input')
    );
    if (!this.input) return;

    this.#controller = new AbortController();
    this.input.setAttribute('autocomplete', 'off');
    this.input.addEventListener('input', this.#handleInput, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#request?.abort();
    this.#handleInput.cancel();
  }

  #handleInput = debounce(() => {
    const term = this.input?.value.trim() ?? '';
    if (term.length < MIN_CHARS) {
      this.#request?.abort();
      this.hidden = true;
      return;
    }
    this.#fetch(term);
  }, 200);

  /** @param {string} term */
  async #fetch(term) {
    this.#request?.abort();
    this.#request = new AbortController();

    const url = new URL(`${this.dataset.url}.json`, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'query,product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');

    try {
      const response = await fetch(url, { signal: this.#request.signal });
      if (!response.ok) throw new Error(`Search suggestions failed: ${response.status}`);
      const { resources } = await response.json();
      this.#render(term, resources?.results ?? {});
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') this.hidden = true;
    }
  }

  /**
   * @param {string} term
   * @param {{ queries?: { text: string, url: string }[], products?: { title: string, url: string, price: string, featured_image?: { url?: string }, image?: string }[] }} results
   */
  #render(term, results) {
    const queries = (results.queries ?? []).filter((query) => query.text.toLowerCase() !== term.toLowerCase());
    // Gift products (title starting with 🎁) are never listed
    const products = (results.products ?? []).filter((product) => !product.title.trim().startsWith('🎁')).slice(0, PRODUCT_LIMIT);

    const queriesGroup = this.querySelector('[data-queries-group]');
    const queriesList = this.querySelector('[data-queries]');
    const productsGroup = this.querySelector('[data-products-group]');
    const productsList = this.querySelector('[data-products]');
    const empty = this.querySelector('[data-empty]');
    const viewAll = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-view-all]'));
    const status = this.querySelector('[data-status]');

    queriesList?.replaceChildren(...queries.map((query) => this.#queryItem(query, term)));
    productsList?.replaceChildren(...products.map((product) => this.#productItem(product)));
    queriesGroup?.toggleAttribute('hidden', queries.length === 0);
    productsGroup?.toggleAttribute('hidden', products.length === 0);
    empty?.toggleAttribute('hidden', queries.length + products.length > 0);

    if (viewAll) {
      const searchUrl = new URL(this.dataset.searchUrl || '/search', location.origin);
      searchUrl.searchParams.set('q', term);
      searchUrl.searchParams.set('type', 'product');
      viewAll.href = searchUrl.toString();
      viewAll.hidden = products.length === 0;
    }

    if (status) {
      status.textContent = queries.length + products.length > 0 ? `Sugestões para ${term}` : `Nenhuma sugestão para ${term}`;
    }

    this.hidden = false;
  }

  /**
   * @param {{ text: string, url: string }} query
   * @param {string} term
   */
  #queryItem(query, term) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = query.url;

    // Highlight what was typed, keep the rest of the suggestion bold
    const start = query.text.toLowerCase().indexOf(term.toLowerCase());
    if (start >= 0) {
      const typed = document.createElement('span');
      typed.className = 'search-page__live-typed';
      typed.textContent = query.text.slice(start, start + term.length);
      link.append(query.text.slice(0, start), typed, query.text.slice(start + term.length));
    } else {
      link.textContent = query.text;
    }

    item.append(link);
    return item;
  }

  /** @param {{ title: string, url: string, price: string, featured_image?: { url?: string }, image?: string }} product */
  #productItem(product) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'search-page__live-product';

    const imageUrl = product.featured_image?.url || product.image;
    const media = document.createElement('span');
    media.className = 'search-page__live-media';
    if (imageUrl) {
      const image = document.createElement('img');
      const src = new URL(imageUrl, location.origin);
      src.searchParams.set('width', '300');
      image.src = src.toString();
      image.alt = '';
      image.loading = 'lazy';
      image.width = 300;
      image.height = 400;
      media.append(image);
    }

    const title = document.createElement('span');
    title.className = 'search-page__live-title';
    title.textContent = product.title;

    const price = document.createElement('span');
    price.className = 'search-page__live-price';
    price.textContent = this.#formatPrice(product.price);

    link.append(media, title, price);
    item.append(link);
    return item;
  }

  /** @param {string} price */
  #formatPrice(price) {
    const value = Number(price);
    if (!Number.isFinite(value)) return price;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
        style: 'currency',
        currency: this.dataset.currency || 'BRL',
      }).format(value);
    } catch {
      return price;
    }
  }
}

if (!customElements.get('search-page-suggestions')) {
  customElements.define('search-page-suggestions', SearchPageSuggestions);
}
